import { useState, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { createCalendar, getCalendarTypes } from 'redux/actions'
import { useHistory } from 'react-router-dom'
import { userInfoSelector, calendarsSelector } from 'redux/selectors'
import { Form, Select, Input, Popover } from 'antd'
import useQuery from 'shared/hooks/useQuery'
import { ICalendar } from '../add-calendar/interfaces'
import { LeftCircleOutlined, CaretDownOutlined } from '@ant-design/icons'
import { RegularButton, Modal } from 'fronton-react'
import { Layout } from 'shared/components/layout/Layout'
import { getFilteredCalendars } from 'shared/api/calendarsApi'
import moment from 'moment'
import { ADD_CALENDAR, MY_CALENDARS } from 'shared/consts'

import './addCalendarForm.scss'

const { Option } = Select

export const AddCalendarForm = () => {
    const history = useHistory()
    const query = useQuery()
    const dispatch = useDispatch()
    const [modal, setModal] = useState<boolean>(false)
    const calendarStore = useSelector(calendarsSelector)
    const userInfoStore = useSelector(userInfoSelector)
    const [form] = Form.useForm()
    const [periodId, setPeriod] = useState<string>('')
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [periodExist, setPeriodExist] = useState<{
        status: boolean
        notification: boolean
    }>({
        status: false,
        notification: false
    })
    const [existedDates, setExistedDates] = useState<ICalendar[]>([])
    const [isStandart, setIsStandart] = useState(false)

    const otherContent = useMemo(() => <span>Данное поле не может быть пустым</span>, [])

    useEffect(() => {
        dispatch(getCalendarTypes.request())
    }, [])

    useEffect(() => {
        if (calendarStore.isCreateSuccess) {
            setIsLoading(false)
            history.push(ADD_CALENDAR)
        }
    }, [calendarStore.isCreateSuccess])

    useEffect(() => {
        (async () => {
            if (!existedDates.length) {
                const list = await getFilteredCalendars({
                    supplierId: localStorage.getItem('supplierId') || '',
                    status: 'Approved'
                })

                const newExistedDates = list.filter((el) => {
                    return el.calendarTypeId && moment(el.createdAt).diff('2022-01-01', 'years') < 1
                })

                if (newExistedDates.length) {
                    setExistedDates(newExistedDates)
                }
            }
        })()
    }, [setExistedDates, existedDates])

    useEffect(() => {
        const type = query.get('type')

        if (type === 'standart') {
            setIsStandart(true)
        }
    }, [query])

    const onFinish = async ({ period, other }: { period: string; other: string }) => {
        const [option] = calendarStore.types.filter(({ id }) => id === period)
        const { typeName } = option || {}
        setIsLoading(true)
        dispatch(
            createCalendar.request({
                name: typeName || other,
                calendarTypeId: typeName ? period : null,
                status: 'Worksheet'
            })
        )
    }

    const onFinishForStandart = () => {
        setModal(true)
    }

    const onModalOk = async () => {
        setModal(false)
        const { period, other } = form.getFieldsValue()
        const [option] = calendarStore.types.filter(({ id }) => id === period)
        const { typeName } = option || {}
        await dispatch(
            createCalendar.request({
                name: typeName || other,
                calendarTypeId: typeName ? period : null,
                status: 'Standard'
            })
        )
        history.push(MY_CALENDARS)
    }

    const onSelectChange = async (value: string) => {
        setIsLoading(true)
        const list = await getFilteredCalendars({
            supplierId: localStorage.getItem('supplierId') || ''
        })
        setIsLoading(false)
        const [isExist] = list.filter(({ calendarTypeId }) => calendarTypeId === value)
        if (isExist) {
            setPeriodExist({
                status: true,
                notification: true
            })
        } else {
            setPeriodExist({
                status: false,
                notification: false
            })
        }
        setPeriod(value)
    }

    return (
        <Layout
            userInfo={userInfoStore}
            showToolbar
            toolbarTitle={
                !isStandart ? 'Добавить календарь' : 'Выбор периода работы по стандартному графику'
            }
        >
            <Form
                form={form}
                onFinish={!isStandart ? onFinish : onFinishForStandart}
                className="calendar-form-page-content"
            >
                <RegularButton
                    variant="pseudo"
                    size="s"
                    iconLeft={<LeftCircleOutlined />}
                    className="back-btn"
                    onClick={() => history.goBack()}
                >
                    Назад
                </RegularButton>
                <div className="select-wrapper">
                    <Form.Item name="period" className="period-form-item">
                        <Select
                            placeholder="Выберите период из списка"
                            className="period-select"
                            onChange={onSelectChange}
                            suffixIcon={<CaretDownOutlined />}
                        >
                            {
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                calendarStore.types.reduce((prev: any, { id, typeName }) => {
                                    if (existedDates.some((el) => el.calendarTypeId === id)) {
                                        return prev
                                    }
                                    return [
                                        ...prev,
                                        <Option key={id} value={id}>
                                            {typeName}
                                        </Option>
                                    ]
                                }, [])
                            }
                            {!isStandart && (
                                <Option value={calendarStore.types.length.toString()}>
                                    Другое
                                </Option>
                            )}
                        </Select>
                    </Form.Item>
                    {periodExist.notification && (
                        <div className="notification">
                            <div style={{ fontWeight: 500, marginBottom: '10px' }}>
                                Для данного периода уже создан календарь. Перейдите в «Мои
                                календари» и продолжите редактирование
                            </div>
                            <RegularButton
                                size="s"
                                variant="primary"
                                className="notification-button"
                                type="button"
                                onClick={() =>
                                    setPeriodExist({
                                        ...periodExist,
                                        notification: false
                                    })
                                }
                            >
                                Ок
                            </RegularButton>
                        </div>
                    )}
                    {periodId === calendarStore.types.length.toString() && (
                        <Popover
                            content={otherContent}
                            placement="rightTop"
                            overlayClassName="calendars-action-popover"
                        >
                            <Form.Item shouldUpdate name="other">
                                <Input className="inputForOther" />
                            </Form.Item>
                        </Popover>
                    )}
                </div>
                <Form.Item shouldUpdate className="submit-container">
                    {() => (
                        <RegularButton
                            size="s"
                            variant="primary"
                            disabled={
                                !form.isFieldsTouched() ||
                                periodExist.status ||
                                (periodId === calendarStore.types.length.toString() &&
                                    !form.getFieldValue('other')) ||
                                isLoading
                            }
                            className="button"
                            type="submit"
                        >
                            {!isStandart ? 'Заполнить период' : 'Подтвердить'}
                        </RegularButton>
                    )}
                </Form.Item>
            </Form>
            <Modal
                size="s"
                key="standartModal"
                className="authorization-modal"
                show={modal}
                closeButton={false}
                onClose={() => setModal(false)}
                overlayType="medium"
            >
                <div className="modal-content">
                    <div className="modal-content-body">
                        Вы отметили, что готовы работать по своему стандартному графику. Нажмите
                        &quot;Ок&quot;, чтобы перейти на форму &quot;Мои календари&quot;
                    </div>
                    <div className="modal-content-actions">
                        <RegularButton
                            size="m"
                            variant="primary"
                            className="modal-content-btn"
                            onClick={onModalOk}
                        >
                            Ок
                        </RegularButton>
                    </div>
                </div>
            </Modal>
        </Layout>
    )
}
